<template>
  <v-container fluid>
    <v-card>
      <v-progress-linear
        v-if="dataLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-card-title>
        <h2 class="text-h5">Link Existing Items</h2>
      </v-card-title>
      <v-card-subtitle>
        Match existing items with subtasks by detecting matches based on viewable locations.
      </v-card-subtitle>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedSchedule"
              :items="schedules"
              item-title="title"
              :return-object="true"
              label="Select Schedule"
              :rules="[v => !!v || 'Schedule is required']"
            />
          </v-col>
        </v-row>

        <!-- Options Section -->
        <v-row class="mt-2">
          <v-col cols="12">
            <v-expansion-panels variant="accordion">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <v-icon class="mr-2">mdi-cog</v-icon>
                  Options
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <TemplateSelector
                        v-model="selectedTemplateIds"
                        :templates="itemTemplates"
                        :processes="processes"
                        :issues="issues"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="d-flex align-center">
                      <v-checkbox
                        v-model="includeLinkedItems"
                        label="Include Linked Items"
                        hint="Show items already linked to other subtasks as potential matches"
                        persistent-hint
                        hide-details="auto"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12">
            <v-btn
              color="primary"
              :disabled="!selectedSchedule"
              @click="startSync"
              :loading="syncInProgress"
            >
              <v-icon left>mdi-sync</v-icon>
              Detect Matches
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="syncResults && syncResults.success" class="mt-4">
          <v-col cols="12" md="6">
            <MatchedItemsProgressBar
              :linked-count="linkedItemsCount"
              :multiple-matches-count="multipleMatchesCount"
              :matched-count="matchedItemsCount"
              :total-subtasks="totalSubtasksCount"
            />
          </v-col>
        </v-row>

        <v-row v-if="syncResults && !syncResults.success" class="mt-4">
          <v-col cols="12">
            <v-alert
              type="error"
              closable
              @click:close="syncResults = null"
            >
              {{ syncResults.message }}
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Matches Dialog -->
    <MatchesDialog
      v-model="matchesDialog"
      :task="selectedTask"
      :matches="getTaskMatches(selectedTask?.id)"
      :subtasks="getTaskSubtasks(selectedTask?.id)"
      :show-linked-column="includeLinkedItems"
      :initial-selections="getTaskSelections(selectedTask?.id)"
      @selection-change="handleSelectionChange"
    />

    <v-card v-if="tasksLoaded" class="mt-4">
      <v-card-text class="pa-0">
        <v-toolbar color="primary" density="compact">
          <v-text-field
            v-model="searchQuery"
            label="Search tasks"
            prepend-inner-icon="mdi-magnify"
            clearable
            @input="filterTasks"
            density="compact"
            hide-details
            class="mx-2"
            style="max-width: 300px;"
          ></v-text-field>

          <v-btn-toggle
            v-model="filterMode"
            mandatory
            density="compact"
            class="mx-2"
          >
            <v-btn value="all" size="small">All</v-btn>
            <v-btn v-if="matchedItemsCount > 0" value="matches" size="small">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #2196f3; margin-right: 6px;"></span>
              Matches
            </v-btn>
            <v-btn v-if="multipleMatchesCount > 0" value="multiple" size="small">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #ff9800; margin-right: 6px;"></span>
              Pending
            </v-btn>
            <v-btn v-if="linkedItemsCount > 0" value="linked" size="small">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #4caf50; margin-right: 6px;"></span>
              Linked
            </v-btn>
          </v-btn-toggle>

          <v-spacer></v-spacer>

          <v-btn icon class="mx-1" @click="expandAll" title="Expand All">
            <v-icon>mdi-unfold-more-horizontal</v-icon>
          </v-btn>

          <v-btn icon class="mx-1" @click="collapseAll" title="Collapse All">
            <v-icon>mdi-unfold-less-horizontal</v-icon>
          </v-btn>
        </v-toolbar>
        <div ref="ganttContainer" style="height: calc(100vh - 300px); width: 100%"></div>
      </v-card-text>
    </v-card>

    <v-row v-if="tasksLoaded" class="mt-4">
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="primary"
          :disabled="!hasLinkableChanges"
          @click="linkItemsDialog = true"
        >
          <v-icon left>mdi-link</v-icon>
          Link
        </v-btn>
      </v-col>
    </v-row>

    <!-- Link Items to Subtasks Dialog -->
    <LinkItemsToSubtasksDialog
      v-model="linkItemsDialog"
      ref="linkItemsDialogRef"
      :linked-count="linkedItemsCount"
      :matched-count="matchedItemsCount"
      :total-subtasks="totalSubtasksCount"
      @confirm="handleLinkConfirm"
    />

    </v-container>
</template>

<script setup>
import { ref, shallowRef, computed, nextTick, onUnmounted, watch } from 'vue';
import { useAppStore } from '@/stores/app';
import { useSettingsStore } from '@/stores/settings';
import { useScheduleStore } from '@/stores/schedule';
import { useTaskStore } from '@/stores/task';
import { useResourceStore } from '@/stores/resource';
import { Gantt } from '@bryntum/gantt';
import '@bryntum/gantt/gantt.material.css';
import TemplateSelector from './dialogs/TemplateSelector.vue';
import MatchedItemsProgressBar from './charts/MatchedItemsProgressBar.vue';
import MatchesDialog from './dialogs/MatchesDialog.vue';
import LinkItemsToSubtasksDialog from './dialogs/LinkItemsToSubtasksDialog.vue';
import '@/styles/LinkExistingItemsPage.css';
import {
  collectLinkedItemIds,
  findAllMatches,
  computeAutoAssignments as computeAutoAssignmentsUtil,
  getTaskMatchCounts as getTaskMatchCountsUtil,
  getUnmatchedCount as getUnmatchedCountUtil,
  calculateSubtaskMatchCounts,
  countTotalLinkedSubtasks,
  countTotalSubtasks
} from '@/utils/LinkMatchesProcessor';
import SchedulePermissions from '@/utils/SchedulePermissions';

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const scheduleStore = useScheduleStore();
const taskStore = useTaskStore();
const resourceStore = useResourceStore();

const selectedSchedule = ref(null);
const syncInProgress = ref(false);
const syncResults = ref(null);
const matchedItems = ref([]);
const tasksLoaded = ref(false);
// Whole task tree, replaced wholesale on load — no need to proxy every node.
const loadedTasks = shallowRef([]);

const ganttContainer = ref(null);
let gantt = null;

const matchesDialog = ref(false);
const linkItemsDialog = ref(false);
const linkItemsDialogRef = ref(null);
const selectedTask = ref(null);
const searchQuery = ref('');
const filterMode = ref('all'); // 'all', 'matches', 'multiple', or 'linked'
const dataLoading = ref(false);

// Track pending selection changes from user interactions
// Key: `${taskId}:${subtaskKey}` -> change object
const pendingChanges = ref(new Map());

// Track auto-assigned selections (computed after matching)
// Key: `${taskId}:${subtaskKey}` -> { itemId, itemType, itemTitle, itemClientId, isAutoAssigned: true }
const autoAssignments = ref(new Map());

// Reverse lookup: which subtask owns each item
// Key: `${itemType}:${itemId}` -> `${taskId}:${subtaskKey}`
const itemToSubtaskMap = ref(new Map());

// Options
const selectedTemplateIds = ref([]);
const includeLinkedItems = ref(false);

// Create permissions instance for filtering linkable schedules
const permissions = computed(() => {
  return new SchedulePermissions({
    permissionRules: [...resourceStore.permissionRules],
    userId: settingsStore.settings.userId,
    teamId: settingsStore.settings.defaultTeamId,
    defaultTeamId: settingsStore.settings.defaultTeamId,
    teamTags: [...resourceStore.teamTags]
  });
});

const schedules = computed(() => {
  const currentSchedules = scheduleStore.schedules?.filter(schedule => schedule.current) || [];
  // Filter to only show schedules the user can link subtasks in
  return permissions.value.filterLinkableSchedules(currentSchedules);
});

const projectCode = computed(() => settingsStore.settings.projectCode);

const processes = computed(() => resourceStore.processes);
const issues = computed(() => resourceStore.issues);
const itemTemplates = computed(() => taskStore.itemTemplates);

// Dictionary for fast template lookup by type and code
// Key format: "issue:CODE" or "process:CODE"
const itemTemplatesByCode = computed(() => {
  const map = {};
  for (const t of itemTemplates.value) {
    let normalizedType;
    if (t.type === 'IssueTemplate') {
      normalizedType = 'issue';
    } else if (t.type === 'ProcessTemplate') {
      normalizedType = 'process';
    } else {
      continue;
    }
    if (t.code) {
      map[`${normalizedType}:${t.code}`] = t;
    }
  }
  return map;
});

// Computed to get selected template codes from selected template IDs
// Templates have code field that matches issueTemplateCode/processTemplateCode
// When no templates are selected, include ALL templates (default to everything)
const selectedTemplateCodes = computed(() => {
  const templatesToUse = selectedTemplateIds.value.length > 0
    ? itemTemplates.value.filter(t => selectedTemplateIds.value.includes(t.id))
    : itemTemplates.value;

  return templatesToUse.reduce((acc, t) => {
    // Map template type to normalized type
    let normalizedType;
    if (t.type === 'IssueTemplate') {
      normalizedType = 'issue';
    } else if (t.type === 'ProcessTemplate') {
      normalizedType = 'process';
    } else {
      return acc;
    }

    if (!acc[normalizedType]) {
      acc[normalizedType] = new Set();
    }
    const templateCode = t.code;
    if (templateCode) {
      acc[normalizedType].add(templateCode);
    }
    return acc;
  }, {});
});

// Build a map of taskId -> matches for quick lookup
const matchesByTaskId = computed(() => {
  const map = {};
  for (const match of matchedItems.value) {
    if (!map[match.taskId]) {
      map[match.taskId] = [];
    }
    map[match.taskId].push(match);
  }
  return map;
});

const getTaskMatches = (taskId) => {
  if (!taskId) return [];
  return matchesByTaskId.value[taskId] || [];
};

const getMatchCount = (taskId) => {
  return getTaskMatches(taskId).length;
};

// Get subtasks for a task from the loaded tasks tree
const getTaskSubtasks = (taskId) => {
  const findTask = (tasks, id) => {
    for (const task of tasks) {
      if (task.id === id) return task;
      if (task.children) {
        const found = findTask(task.children, id);
        if (found) return found;
      }
    }
    return null;
  };
  const task = findTask(loadedTasks.value, taskId);
  return task?.subtasks || [];
};

const showMatches = (task) => {
  selectedTask.value = task;
  matchesDialog.value = true;
};

// Get all current selections for a task (combining auto-assignments and user changes)
// Returns a map of subtaskKey -> itemId
const getTaskSelections = (taskId) => {
  if (!taskId) return {};

  const selections = {};
  const matches = getTaskMatches(taskId);

  // Get unique subtask keys for this task
  const subtaskKeys = new Set();
  for (const match of matches) {
    subtaskKeys.add(`${match.subtaskTitle}|${match.itemType}`);
  }

  // For each subtask, get the current selection
  for (const subtaskKey of subtaskKeys) {
    const fullKey = `${taskId}:${subtaskKey}`;

    // Check for user selection first
    const userChange = pendingChanges.value.get(fullKey);
    if (userChange) {
      selections[subtaskKey] = userChange.newItemId;
      continue;
    }

    // Fall back to auto-assignment
    const autoAssignment = autoAssignments.value.get(fullKey);
    if (autoAssignment) {
      selections[subtaskKey] = autoAssignment.itemId;
    }
  }

  return selections;
};

// Handle selection changes from MatchesDialog
const handleSelectionChange = ({ taskId, changes }) => {
  const affectedTaskIds = new Set([taskId]);

  // Create new Maps to ensure reactivity is triggered
  const newPendingChanges = new Map(pendingChanges.value);
  const newAutoAssignments = new Map(autoAssignments.value);
  const newItemToSubtaskMap = new Map(itemToSubtaskMap.value);

  // Update pending changes for this task
  // Each change is keyed by taskId:subtaskKey to allow overwrites when user changes selection again
  for (const change of changes) {
    const key = `${taskId}:${change.subtaskKey}`;

    // Handle deselection (unlink) case - newItemId is null
    if (change.newItemId === null) {
      // Remove the previous item from ownership map if it exists
      if (change.previousItemId) {
        const prevItemKey = `${change.itemType}:${change.previousItemId}`;
        newItemToSubtaskMap.delete(prevItemKey);
      }

      // Clear any auto-assignment for this subtask
      newAutoAssignments.delete(key);

      // Track the deselection as a pending change
      newPendingChanges.set(key, change);
      continue;
    }

    // Check if this item is already assigned to another subtask
    const itemKey = `${change.itemType}:${change.newItemId}`;
    const existingOwner = newItemToSubtaskMap.get(itemKey);

    if (existingOwner && existingOwner !== key) {
      // Item is owned by another subtask - need to unassign it from there
      // existingOwner format: "taskId:subtaskTitle|itemType"
      const colonIndex = existingOwner.indexOf(':');
      const ownerTaskIdStr = existingOwner.substring(0, colonIndex);
      // Only parse as int if the entire string is numeric
      const ownerTaskId = /^\d+$/.test(ownerTaskIdStr) ? parseInt(ownerTaskIdStr, 10) : ownerTaskIdStr;

      // Remove auto-assignment from the other subtask
      newAutoAssignments.delete(existingOwner);

      // Only remove pending change if it was for THIS item being taken
      // If the other subtask has a pending change to a DIFFERENT item, keep it
      const existingOwnerPendingChange = newPendingChanges.get(existingOwner);
      if (existingOwnerPendingChange && existingOwnerPendingChange.newItemId === change.newItemId) {
        newPendingChanges.delete(existingOwner);
      }

      // Update the item ownership - remove this item from the old owner
      newItemToSubtaskMap.delete(itemKey);

      // Track that we need to update that task's Gantt row too
      affectedTaskIds.add(ownerTaskId);
    }

    // Remove the old item ownership for this subtask if it had a different selection
    // Check what this subtask previously owned (from auto-assignment or previous pending change)
    const previousAutoAssignment = newAutoAssignments.get(key);
    const previousPendingChange = newPendingChanges.get(key);

    // Determine what item this subtask previously owned
    let previousOwnedItemId = null;
    if (previousPendingChange && previousPendingChange.newItemId) {
      previousOwnedItemId = previousPendingChange.newItemId;
    } else if (previousAutoAssignment) {
      previousOwnedItemId = previousAutoAssignment.itemId;
    }

    // If switching to a different item, remove the old ownership
    if (previousOwnedItemId && previousOwnedItemId !== change.newItemId) {
      const previousItemKey = `${change.itemType}:${previousOwnedItemId}`;
      newItemToSubtaskMap.delete(previousItemKey);
    }

    // If the new selection matches the original linked item (user reverted), remove from pending
    if (change.newItemId === change.previousItemId && change.wasLinked) {
      newPendingChanges.delete(key);
    } else {
      newPendingChanges.set(key, change);

      // Update item ownership to this subtask
      newItemToSubtaskMap.set(itemKey, key);
    }
  }

  // Remove entries for subtasks that are no longer in the changes array
  // (user may have deselected or reverted to original)
  const currentSubtaskKeys = new Set(changes.map(c => `${taskId}:${c.subtaskKey}`));
  for (const [key] of newPendingChanges) {
    if (key.startsWith(`${taskId}:`) && !currentSubtaskKeys.has(key)) {
      // Check if this subtask still has a selection - if not, it was reverted
      const subtaskKey = key.substring(taskId.toString().length + 1);
      const changeStillExists = changes.some(c => c.subtaskKey === subtaskKey);
      if (!changeStillExists) {
        newPendingChanges.delete(key);
      }
    }
  }

  // Assign new Maps to trigger reactivity
  pendingChanges.value = newPendingChanges;
  autoAssignments.value = newAutoAssignments;
  itemToSubtaskMap.value = newItemToSubtaskMap;

  // Update Gantt rows for all affected tasks
  for (const affectedTaskId of affectedTaskIds) {
    updateGanttRow(affectedTaskId);
  }
};

// Update a single Gantt row's match counts
const updateGanttRow = (taskId) => {
  if (!gantt) return;

  const record = gantt.taskStore.getById(taskId);
  if (!record) return;

  // Recalculate counts for this task
  const { linked, multiple, matched } = getTaskMatchCounts(taskId);

  // Update the record data
  record.set({
    linkedCount: linked,
    multipleMatchesCount: multiple,
    matchedCount: matched
  });
};

// Get counts per task for linked, multiple, matched (single), and unmatched subtasks
const getTaskMatchCounts = (taskId) => {
  const matches = getTaskMatches(taskId);
  return getTaskMatchCountsUtil(taskId, matches, pendingChanges.value, autoAssignments.value);
};

// Count unmatched subtasks for a task (subtasks that have no matches)
const getUnmatchedCount = (task) => {
  const matches = getTaskMatches(task.id);
  return getUnmatchedCountUtil(task, matches);
};

// Transform tasks for Gantt display
const transformTasks = (tasks) => {
  return tasks.map(task => {
    const matchCount = getMatchCount(task.id);
    const { linked, multiple, matched } = getTaskMatchCounts(task.id);
    const unmatched = getUnmatchedCount(task);
    const hasSubtasks = task.subtasks && task.subtasks.length > 0;
    return {
      id: task.id,
      title: task.title,
      code: task.code,
      children: task.children ? transformTasks(task.children) : undefined,
      leaf: !task.children?.length,
      expanded: false,
      matchCount,
      linkedCount: linked,
      multipleMatchesCount: multiple,
      matchedCount: matched,
      unmatchedCount: unmatched,
      hasSubtasks,
      manuallyScheduled: true,
      draggable: false
    };
  });
};

const initGantt = () => {
  if (!ganttContainer.value || gantt) return;

  const columns = [
    { text: 'Code', field: 'code', readOnly: true, treeColumn: true, width: 150 },
    { text: 'Title', field: 'title', width: 300, readOnly: true },
    {
      field: 'matches',
      text: 'Matches',
      type: 'template',
      readOnly: true,
      width: 180,
      template: ({ record }) => {
        if (!record.hasSubtasks) return '';

        const linked = record.linkedCount || 0;
        const multiple = record.multipleMatchesCount || 0;
        const matched = record.matchedCount || 0;
        const unmatched = record.unmatchedCount || 0;

        const chipStyle = 'display:inline-flex;align-items:center;justify-content:center;border-radius:1em;padding:0.2em 0.6em;color:#fff;font-weight:500;font-size:0.85em;cursor:pointer;min-width:24px;margin-right:4px;';

        return `<div style="display:flex;align-items:center;">
          <span style="${chipStyle}background-color:#4caf50;">${linked}</span>
          <span style="${chipStyle}background-color:#ff9800;">${multiple}</span>
          <span style="${chipStyle}background-color:#2196f3;">${matched}</span>
          <span style="${chipStyle}background-color:#9e9e9e;">${unmatched}</span>
        </div>`;
      }
    }
  ];

  const config = {
    appendTo: ganttContainer.value,
    columns,
    features: {
      taskEdit: false,
      dependencies: false,
      rowReorder: false,
      filter: true,
      taskMenu: false
    },
    subGridConfigs: {
      locked: {
        width: '100%'
      },
      normal: {
        hidden: true
      }
    },
    project: {
      taskStore: {
        tree: true,
        fields: ['id', 'title', 'code', 'matchCount', 'linkedCount', 'multipleMatchesCount', 'matchedCount', 'unmatchedCount', 'hasSubtasks'],
        rootNode: {
          expanded: true,
          children: transformTasks(loadedTasks.value)
        }
      }
    },
    listeners: {
      cellClick: ({ record, column }) => {
        if (column.field === 'matches' && record.matchCount > 0) {
          showMatches(record);
        }
      }
    }
  };

  gantt = new Gantt(config);
};

const expandAll = () => {
  if (gantt) {
    gantt.expandAll();
  }
};

const collapseAll = () => {
  if (gantt) {
    gantt.collapseAll();
  }
};

const filterTasks = () => {
  if (!gantt) return;

  const searchFilter = ({ title, code }) =>
    !searchQuery.value ||
    title?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    code?.toLowerCase().includes(searchQuery.value.toLowerCase());

  const modeFilter = (task) => {
    if (filterMode.value === 'all') return true;
    if (filterMode.value === 'matches') return task.matchedCount > 0 || task.multipleMatchesCount > 0;
    if (filterMode.value === 'multiple') return task.multipleMatchesCount > 0;
    if (filterMode.value === 'linked') return task.linkedCount > 0;
    return true;
  };

  gantt.taskStore.filter(task => searchFilter(task) && modeFilter(task));
};

watch([searchQuery, filterMode], () => {
  filterTasks();
});

// Load processes/issues when schedule is selected so TemplateSelector can filter
watch(selectedSchedule, (newSchedule) => {
  if (newSchedule) {
    loadData();
  }
});

const fetchPaginatedData = async (endpoint) => {
  const allData = [];
  let page = 1;
  const pageSize = 10000;
  let hasMoreData = true;

  while (hasMoreData) {
    try {
      const url = `/api/${projectCode.value}${endpoint}?page=${page}&pageSize=${pageSize}`;
      const response = await appStore.apiClient.get(url);

      if (response && response.length > 0) {
        allData.push(...response);
        page += 1;
      } else {
        hasMoreData = false;
      }
    } catch (error) {
      console.error(`Error fetching ${endpoint} page ${page}:`, error);
      hasMoreData = false;
    }
  }

  return allData;
};

const loadData = async () => {
  // Only fetch if data not already in store
  if (processes.value.length > 0 && issues.value.length > 0) {
    return;
  }

  dataLoading.value = true;

  try {
    const [processesData, issuesData] = await Promise.all([
      fetchPaginatedData('/bi/processes'),
      fetchPaginatedData('/bi/issues')
    ]);

    resourceStore.setProcesses(processesData);
    resourceStore.setIssues(issuesData);
  } catch (error) {
    console.error('Error loading data:', error);
  } finally {
    dataLoading.value = false;
  }
};


// Store for linked item IDs (populated before matching)
const linkedIssueIdsRef = ref(new Set());
const linkedProcessIdsRef = ref(new Set());
// Map of "taskId:itemType:templateCode" -> Set of linked item IDs for that specific subtask
const linkedBySubtaskRef = ref(new Map());

// Computed: count of already-linked subtasks across ALL templates (not filtered)
// This should remain constant regardless of template filter selection
const totalLinkedSubtasksCount = computed(() => countTotalLinkedSubtasks(loadedTasks.value));

// Count pending unlinks - changes where a linked item was deselected or changed to a different item
const pendingUnlinksCount = computed(() => {
  let count = 0;
  for (const [, change] of pendingChanges.value) {
    // Count as unlink if:
    // 1. User explicitly deselected (newItemId is null) AND was previously linked
    // 2. User changed to a different item from the linked one
    if (change.wasLinked) {
      if (change.newItemId === null || change.newItemId !== change.previousItemId) {
        count++;
      }
    }
  }
  return count;
});

// Computed properties for progress bar - count per subtask, not per match
// Subtasks with auto-assignments or user selections count as "matched" (blue)
// Subtasks with multiple matches and no assignment count as "multiple" (orange)
// Subtasks that were linked but user re-linked to a different item count as "matched" (blue)
const subtaskMatchCounts = computed(() =>
  calculateSubtaskMatchCounts(matchedItems.value, pendingChanges.value, autoAssignments.value)
);

// Linked count is calculated from ALL subtasks, minus pending unlinks
// This reflects the current state after user changes (before saving)
const linkedItemsCount = computed(() => totalLinkedSubtasksCount.value - pendingUnlinksCount.value);
const multipleMatchesCount = computed(() => subtaskMatchCounts.value.multiple);
const matchedItemsCount = computed(() => subtaskMatchCounts.value.matched);
const totalSubtasksCount = computed(() => countTotalSubtasks(loadedTasks.value));

// Check if there are any changes to link (pending changes or new auto-assignments)
const hasLinkableChanges = computed(() => {
  // Check for pending user changes
  if (pendingChanges.value.size > 0) {
    return true;
  }

  // Check for auto-assignments that aren't already linked
  for (const [, assignment] of autoAssignments.value) {
    if (!assignment.isLinked) {
      return true;
    }
  }

  return false;
});

const startSync = async () => {
  if (!selectedSchedule.value) {
    return;
  }

  syncInProgress.value = true;
  syncResults.value = null;
  matchedItems.value = [];
  tasksLoaded.value = false;
  pendingChanges.value = new Map(); // Clear pending changes on new sync
  autoAssignments.value = new Map(); // Clear auto-assignments on new sync
  itemToSubtaskMap.value = new Map(); // Clear item ownership map

  // Destroy existing gantt if present
  if (gantt) {
    gantt.destroy();
    gantt = null;
  }

  try {
    // Load processes, issues, and templates data first
    await loadData();

    // Fetch tasks for the selected schedule
    const path = `/api/${projectCode.value}/tasks?scheduleIds=${selectedSchedule.value.id}&nested=true`;
    const tasks = await appStore.apiClient.get(path);

    scheduleStore.setScheduleTasksObj({ [selectedSchedule.value.id]: tasks });
    taskStore.setTasks([...tasks]);

    // Collect all linked item IDs from subtasks before matching
    const linkedIds = collectLinkedItemIds(tasks);
    linkedIssueIdsRef.value = linkedIds.linkedIssueIds;
    linkedProcessIdsRef.value = linkedIds.linkedProcessIds;
    linkedBySubtaskRef.value = linkedIds.linkedBySubtask;

    // Find matching items using utility function
    const allMatches = findAllMatches(tasks, {
      selectedTemplateCodes: selectedTemplateCodes.value,
      issues: issues.value,
      processes: processes.value,
      itemTemplatesByCode: itemTemplatesByCode.value,
      includeLinkedItems: includeLinkedItems.value,
      linkedIssueIds: linkedIssueIdsRef.value,
      linkedProcessIds: linkedProcessIdsRef.value,
      linkedBySubtask: linkedBySubtaskRef.value
    });

    matchedItems.value = allMatches;
    loadedTasks.value = tasks;
    tasksLoaded.value = true;

    // Run auto-assignment algorithm using utility function
    const assignments = computeAutoAssignmentsUtil(allMatches);
    autoAssignments.value = assignments.autoAssignments;
    itemToSubtaskMap.value = assignments.itemToSubtaskMap;

    syncResults.value = {
      success: true,
      message: `Found ${allMatches.length} matching items for schedule: ${selectedSchedule.value.title}`
    };

    // Initialize Gantt after DOM updates and reapply filters
    nextTick(() => {
      initGantt();
      filterTasks();
    });
  } catch (error) {
    console.error('Sync error:', error);
    syncResults.value = {
      success: false,
      message: `Failed to synchronize items: ${error.message}`
    };
  } finally {
    syncInProgress.value = false;
  }
};

// Build the payload for linking items to subtasks
const buildLinkPayload = () => {
  const payload = [];

  // Helper to find a subtask by title and itemType within a task
  const findSubtask = (task, subtaskTitle, itemType) => {
    if (!task?.subtasks) return null;
    return task.subtasks.find(s => s.title === subtaskTitle && s.itemType === itemType);
  };

  // Helper to find a task by ID in the loaded tasks tree
  const findTask = (tasks, taskId) => {
    for (const task of tasks) {
      if (task.id === taskId) return task;
      if (task.children) {
        const found = findTask(task.children, taskId);
        if (found) return found;
      }
    }
    return null;
  };

  // Process all auto-assignments and pending changes
  // Format: subtaskKey = "taskId:subtaskTitle|itemType"
  const processedSubtasks = new Set();

  // First, process pending changes (user selections override auto-assignments)
  for (const [key, change] of pendingChanges.value) {
    const colonIndex = key.indexOf(':');
    const taskId = key.substring(0, colonIndex);
    const subtaskKey = key.substring(colonIndex + 1);
    const [subtaskTitle, itemType] = subtaskKey.split('|');

    const task = findTask(loadedTasks.value, taskId);
    const subtask = findSubtask(task, subtaskTitle, itemType);

    if (subtask) {
      payload.push({
        taskId: taskId,
        subtaskId: subtask.id,
        itemId: change.newItemId || '',
        leadByTeamId: subtask.leadByTeamId || ''
      });
      processedSubtasks.add(key);
    }
  }

  // Then, process auto-assignments that weren't overridden by pending changes
  for (const [key, assignment] of autoAssignments.value) {
    // Skip if already processed via pending changes
    if (processedSubtasks.has(key)) continue;

    // Skip if already linked (no change needed)
    if (assignment.isLinked) continue;

    const colonIndex = key.indexOf(':');
    const taskId = key.substring(0, colonIndex);
    const subtaskKey = key.substring(colonIndex + 1);
    const [subtaskTitle, itemType] = subtaskKey.split('|');

    const task = findTask(loadedTasks.value, taskId);
    const subtask = findSubtask(task, subtaskTitle, itemType);

    if (subtask) {
      payload.push({
        taskId: taskId,
        subtaskId: subtask.id,
        itemId: assignment.itemId || '',
        leadByTeamId: subtask.leadByTeamId || ''
      });
    }
  }

  return payload;
};

// Handle confirm from LinkItemsToSubtasksDialog
const handleLinkConfirm = async () => {
  try {
    const payload = buildLinkPayload();

    if (payload.length === 0) {
      linkItemsDialogRef.value?.setProcessing(false);
      linkItemsDialog.value = false;
      return;
    }

    await appStore.apiClient.patch(
      `/api/${projectCode.value}/tasks/subtasks`,
      payload
    );

    // Clear pending changes after successful save
    pendingChanges.value = new Map();

    // Close dialog and reset processing state
    linkItemsDialogRef.value?.setProcessing(false);
    linkItemsDialog.value = false;

    // Refresh the schedule data
    await startSync();
  } catch (error) {
    console.error('Error linking items to subtasks:', error);
    linkItemsDialogRef.value?.setProcessing(false);
  }
};

onUnmounted(() => {
  if (gantt) {
    gantt.destroy();
  }
});
</script>